import React from "react";
import { useNavigate } from "react-router-dom";
import ReportComponentLibraryController from "./ReportComponentLibraryController";
import { RENDER_TYPES } from "./ReportComponentDTO";

/**
 * 🧱 REPORT COMPONENT LIBRARY (Group ESG admin screen)
 * Create reusable, typed report components. Authoring order:
 *   1. bind the disclosure(s) this component reports (taxonomy concepts, min 1)
 *   2. for each, give it a label, choose how it presents, add guidance
 */
const discKey = (d) => d.rowId || `free:${d.measureName}`;

/**
 * Search + add control for binding taxonomy disclosures to the component.
 * Falls back to a free-typed disclosure when no taxonomy is loaded / no match.
 */
const normKey = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/** The technical name only counts as a "code" column when it is NOT just the
 *  label with spaces/punctuation stripped. */
const codeFor = (concept) =>
  normKey(concept.localName) === normKey(concept.cleanLabel)
    ? ""
    : concept.localName || "";

/**
 * Client-side boolean text matcher over the already-loaded list — there is no
 * database round-trip and no SQL, so nothing to inject. Supported:
 *   space  → all words must match (AND)
 *   OR / | → alternatives
 *   -word / NOT word → exclude
 *   "exact phrase"   → phrase match
 *   *  or  %          → wildcard
 */
function buildMatcher(query) {
  const raw = (query || "").trim();
  if (!raw) return () => true;

  const groups = raw
    .split(/\s+(?:OR|\|\|?)\s+/i)
    .map((g) => g.trim())
    .filter(Boolean)
    .map((g) => {
      const toks = g.match(/"[^"]+"|\S+/g) || [];
      const inc = [];
      const exc = [];
      let negNext = false;
      toks.forEach((tok) => {
        if (/^NOT$/i.test(tok)) {
          negNext = true;
          return;
        }
        let neg = negNext;
        negNext = false;
        let t = tok;
        if (t.startsWith("-")) {
          neg = true;
          t = t.slice(1);
        }
        t = t.replace(/^\+/, "").replace(/^"|"$/g, "").trim().toLowerCase();
        if (!t || ["and", "&", "&&"].includes(t)) return;
        (neg ? exc : inc).push(t);
      });
      return { inc, exc };
    });

  const test = (hay, term) => {
    if (/[*%]/.test(term)) {
      const rx = new RegExp(
        term.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/[*%]/g, ".*"),
        "i",
      );
      return rx.test(hay);
    }
    return hay.includes(term);
  };

  return (hay) => {
    const h = (hay || "").toLowerCase();
    return groups.some(
      ({ inc, exc }) =>
        inc.every((t) => test(h, t)) && exc.every((t) => !test(h, t)),
    );
  };
}

const CAP = 200;

function DisclosurePicker({ concepts, loading, note, chosen, onAdd }) {
  const [q, setQ] = React.useState("");
  const [kinds, setKinds] = React.useState(() => new Set());
  const [checked, setChecked] = React.useState({}); // discKey -> concept
  const [showCodes, setShowCodes] = React.useState(false);

  const chosenKeys = React.useMemo(
    () => new Set((chosen || []).map(discKey)),
    [chosen],
  );

  // 1. text query
  const textMatched = React.useMemo(() => {
    const match = buildMatcher(q);
    return concepts.filter(
      (c) =>
        !chosenKeys.has(discKey(c)) &&
        match(`${c.cleanLabel} ${c.localName}`),
    );
  }, [q, concepts, chosenKeys]);

  // 2. object-type facets from the text result — names only, alphabetical
  const facets = React.useMemo(
    () =>
      [...new Set(textMatched.map((c) => c.kind))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [textMatched],
  );

  // 3. text result narrowed by any active facet
  const matches = React.useMemo(
    () =>
      kinds.size === 0
        ? textMatched
        : textMatched.filter((c) => kinds.has(c.kind)),
    [textMatched, kinds],
  );

  const shown = matches.slice(0, CAP);
  const checkedList = Object.values(checked);

  const toggle = (concept) =>
    setChecked((p) => {
      const n = { ...p };
      const k = discKey(concept);
      if (n[k]) delete n[k];
      else n[k] = concept;
      return n;
    });

  const toggleKind = (k) =>
    setKinds((p) => {
      const n = new Set(p);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const clearAfterAdd = () => {
    setChecked({});
    setQ("");
    setKinds(new Set());
  };

  const addChecked = () => {
    // append in the order the rows appear in the list, not tick order
    matches.filter((m) => checked[discKey(m)]).forEach((m) => onAdd(m));
    clearAfterAdd();
  };

  const addAll = () => {
    if (
      matches.length > 50 &&
      !window.confirm(`Add all ${matches.length} disclosures to this component?`)
    ) {
      return;
    }
    matches.forEach((m) => onAdd(m));
    clearAfterAdd();
  };

  const exactExists = concepts.some(
    (c) => (c.cleanLabel || "").toLowerCase() === q.trim().toLowerCase(),
  );

  // Only offer the technical-name toggle when at least one row in the current
  // result actually carries one (many rows won't).
  const anyCode = matches.some((m) => codeFor(m));

  return (
    <div className="border rounded p-2 mb-3 bg-light">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="extra-small fw-bold text-uppercase text-muted">
          Disclosures this component reports (at least one)
        </span>
        {loading && <span className="spinner-border spinner-border-sm text-primary" />}
      </div>

      {chosen.length === 0 ? (
        <p className="text-muted extra-small mb-2">
          Bind at least one. Filter the list, pick a type, tick rows, then
          Add&nbsp;selected — each one gets its own block below.
        </p>
      ) : (
        <p className="text-muted extra-small mb-2">
          {chosen.length} bound. Add more below, or edit each one&apos;s block
          further down.
        </p>
      )}

      {note && <div className="extra-small text-muted mb-1">{note}</div>}

      {/* 1. free-text filter — kept clear of other content top and bottom */}
      <div className="my-3">
        <input
          className="form-control form-control-sm"
          placeholder='Filter — e.g. water AND discharge, "scope 1", emission*'
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {concepts.length > 0 && (
        <>
          {/* 2. object-type facets from the current text result — names only */}
          {facets.length > 1 && (
            <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
              <span className="extra-small fw-bold text-uppercase text-muted">
                Type
              </span>
              {facets.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`btn btn-sm rounded-pill extra-small px-3 py-0 ${
                    kinds.has(f) ? "btn-dark" : "btn-outline-secondary"
                  }`}
                  style={{ lineHeight: "1.9" }}
                  onClick={() => toggleKind(f)}
                >
                  {f}
                </button>
              ))}
              {kinds.size > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm extra-small p-0"
                  onClick={() => setKinds(new Set())}
                >
                  clear
                </button>
              )}
            </div>
          )}

          {anyCode && (
            <div className="d-flex justify-content-end mb-1">
              <button
                type="button"
                className="btn btn-sm btn-link p-0 extra-small text-decoration-none"
                onClick={() => setShowCodes((v) => !v)}
              >
                {showCodes ? "hide technical names" : "show technical names"}
              </button>
            </div>
          )}

          {/* 3. checkbox list — one datapoint per row; technical name is an
                optional second row, hidden by default; zebra striped */}
          <div
            className="border rounded bg-white"
            style={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden" }}
          >
            {shown.length === 0 ? (
              <div className="extra-small text-muted p-2 fst-italic">
                No matching datapoints.
              </div>
            ) : (
              shown.map((m, i) => {
                const key = discKey(m);
                const code = codeFor(m);
                return (
                  <div
                    key={key}
                    className="border-bottom small"
                    style={{ background: i % 2 ? "#ffffff" : "#f4f5f7" }}
                  >
                    <label
                      className="d-flex px-2 py-1 m-0"
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input flex-shrink-0 mt-1"
                        checked={!!checked[key]}
                        onChange={() => toggle(m)}
                      />
                      <span
                        className="ps-2 flex-grow-1"
                        style={{ minWidth: 0, wordBreak: "break-word" }}
                      >
                        {m.cleanLabel}
                      </span>
                    </label>
                    {showCodes && code && (
                      <div className="d-flex px-2 pb-1">
                        <span
                          className="form-check-input flex-shrink-0"
                          style={{ visibility: "hidden" }}
                          aria-hidden="true"
                        />
                        <span
                          className="ps-2 extra-small text-muted font-monospace"
                          style={{ wordBreak: "break-all" }}
                        >
                          {code}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <span className="extra-small text-muted">
              {matches.length > shown.length
                ? `Showing ${shown.length} of ${matches.length.toLocaleString()} — narrow the filter`
                : `${matches.length.toLocaleString()} match${
                    matches.length === 1 ? "" : "es"
                  }`}
              {checkedList.length > 0 && ` · ${checkedList.length} ticked`}
            </span>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                disabled={matches.length === 0}
                onClick={addAll}
              >
                Add all ({matches.length.toLocaleString()})
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary fw-bold"
                disabled={checkedList.length === 0}
                onClick={addChecked}
              >
                Add selected{checkedList.length ? ` (${checkedList.length})` : ""}
              </button>
            </div>
          </div>
        </>
      )}

      {q.trim() && !exactExists && (
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary mt-2"
          onClick={() => {
            onAdd({ rowId: null, xbrlTag: "", measureName: q.trim(), headerId: null });
            setQ("");
          }}
        >
          + Add &ldquo;{q.trim()}&rdquo; as a manual disclosure
        </button>
      )}
    </div>
  );
}

export default function ReportComponentLibraryView() {
  const navigate = useNavigate();
  const c = ReportComponentLibraryController();
  const d = c.draft;

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-2">
              <div className="col">
                <h2 className="h4 fw-bold mb-1">Report Component Library</h2>
                <p className="text-muted small mb-0">
                  Reusable, typed building blocks that report templates assemble
                  into sections and pillars
                </p>
              </div>
            </div>

            {c.error && (
              <div className="alert alert-danger extra-small">{c.error}</div>
            )}
            {c.notice && (
              <div className="alert alert-info extra-small">{c.notice}</div>
            )}

            <div className="row g-3">
              {/* ---------------- LIST ---------------- */}
              <div className="col-md-4">
                <div className="card shadow-sm border-0 bg-white p-3 h-100">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">
                      Components ({c.allCount})
                    </h6>
                    <button
                      className="btn btn-sm btn-primary fw-bold"
                      onClick={c.newComponent}
                    >
                      + New
                    </button>
                  </div>

                  {c.disclosures.length > 0 && (
                    <select
                      className="form-select form-select-sm mb-2"
                      value={c.disclosureFilter}
                      onChange={(e) => c.setDisclosureFilter(e.target.value)}
                    >
                      <option value="">All disclosures</option>
                      {c.disclosures.map((disc) => (
                        <option key={disc} value={disc}>
                          {disc}
                        </option>
                      ))}
                    </select>
                  )}

                  {c.loading ? (
                    <div className="text-center py-4">
                      <span className="spinner-border spinner-border-sm text-primary" />
                    </div>
                  ) : c.components.length === 0 ? (
                    <p className="text-muted extra-small fst-italic mb-0">
                      No components yet. Create the first one.
                    </p>
                  ) : (
                    <div className="list-group list-group-flush">
                      {c.components.map((row) => (
                        <button
                          key={row.id}
                          className={`list-group-item list-group-item-action px-2 py-2 ${
                            d?.id === row.id ? "active" : ""
                          }`}
                          onClick={() => c.editComponent(row.id)}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <span className="fw-bold small">{row.label}</span>
                            {String(row.id).startsWith("seed_") ? (
                              <span className="badge bg-info text-dark">example</span>
                            ) : (
                              !row.isActive && (
                                <span className="badge bg-secondary">retired</span>
                              )
                            )}
                          </div>
                          <div className="extra-small text-muted">
                            {row.disclosureCount} disclosure
                            {row.disclosureCount === 1 ? "" : "s"}
                            {row.primaryDisclosure
                              ? ` · ${row.primaryDisclosure}`
                              : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ---------------- EDITOR ---------------- */}
              <div className="col-md-8">
                {!d ? (
                  <div className="card shadow-sm border-0 bg-white p-4 h-100 d-flex align-items-center justify-content-center">
                    <p className="text-muted small mb-0">
                      Select a component to edit, or create a new one.
                    </p>
                  </div>
                ) : (
                  <div className="card shadow-sm border-0 bg-white p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">
                        {c.isNew ? "New Component" : `Edit — v${d.version}`}
                      </h6>
                      <div className="d-flex gap-2">
                        {!c.isNew && d.isActive && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => c.deactivate(d.id)}
                          >
                            Retire
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={c.cancelEdit}
                        >
                          Close
                        </button>
                        <button
                          className="btn btn-sm btn-primary fw-bold"
                          disabled={!c.canSave}
                          onClick={c.save}
                        >
                          {c.saving ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>

                    {/* 1. bind the disclosure(s) this component reports */}
                    <DisclosurePicker
                      concepts={c.concepts}
                      loading={c.conceptsLoading}
                      note={c.conceptsNote}
                      chosen={d.boundDisclosures}
                      onAdd={c.addDisclosure}
                    />

                    {/* 2. one presentation block per bound disclosure,
                          in selection order */}
                    {d.boundDisclosures.map((disc, i) => (
                      <div
                        key={disc.id}
                        className="border rounded p-3 mb-3 bg-white"
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <span className="extra-small fw-bold text-uppercase text-primary">
                              Disclosure {i + 1}
                            </span>
                            <div className="small fw-semibold">
                              {disc.measureName || disc.xbrlTag}
                              {disc.rowId ? "" : " (manual)"}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => c.removeDisclosure(disc.id)}
                            title="Remove this disclosure"
                          >
                            Remove
                          </button>
                        </div>

                        <div className="row g-2">
                          <div className="col-md-8">
                            <label className="extra-small fw-bold text-muted mb-1">
                              Label
                            </label>
                            <input
                              className="form-control form-control-sm"
                              value={disc.label}
                              onChange={(e) =>
                                c.setDisclosureField(disc.id, "label", e.target.value)
                              }
                              placeholder="e.g. Gross Scope 1 GHG emissions"
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="extra-small fw-bold text-muted mb-1">
                              Presents as
                            </label>
                            <select
                              className="form-select form-select-sm"
                              value={disc.renderType}
                              onChange={(e) =>
                                c.setDisclosureField(
                                  disc.id,
                                  "renderType",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">Choose option…</option>
                              {[...RENDER_TYPES]
                                .sort((a, b) => a.label.localeCompare(b.label))
                                .map((r) => (
                                  <option key={r.code} value={r.code}>
                                    {r.label}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div className="col-12">
                            <label className="extra-small fw-bold text-muted mb-1">
                              Guidance shown to the person filling it in
                            </label>
                            <textarea
                              className="form-control form-control-sm"
                              rows={2}
                              value={disc.guidance}
                              onChange={(e) =>
                                c.setDisclosureField(
                                  disc.id,
                                  "guidance",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {c.validationErrors.length > 0 && (
                      <ul className="text-danger extra-small mt-3 mb-0">
                        {c.validationErrors.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
              <button
                className="btn btn-link text-decoration-none p-0 text-secondary small"
                onClick={() => navigate("/registered/site-admin")}
              >
                <i className="bi bi-arrow-left me-2"></i> Return to Site
                Administration
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
